import React, { useState } from 'react';
import { Button } from './ui/button';
import { apiService, formatApiError } from '../services/api';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { ArrowLeft, AlertTriangle, Send, CheckCircle, User, MessageCircle, Shield, Bug } from 'lucide-react';
import { BrandLockup } from './BrandLogo';

const ReportIssues = ({ onBack }) => {
  const [formData, setFormData] = useState({
    reportType: '',
    userReported: '',
    description: '',
    evidence: '',
    contactInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const reportTypes = [
    { value: 'harassment', label: 'Harassment or Bullying', icon: AlertTriangle, color: 'text-red-500' },
    { value: 'fake-profile', label: 'Fake Profile or Scam', icon: User, color: 'text-orange-500' },
    { value: 'inappropriate-content', label: 'Inappropriate Content', icon: MessageCircle, color: 'text-purple-500' },
    { value: 'safety-concern', label: 'Safety Concern', icon: Shield, color: 'text-blue-500' },
    { value: 'technical-issue', label: 'Technical Problem', icon: Bug, color: 'text-green-500' },
    { value: 'other', label: 'Other Issue', icon: AlertTriangle, color: 'text-gray-500' }
  ];

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    setError('');
    const categoryMap = { 'harassment': 'abuse', 'fake-profile': 'safety', 'inappropriate-content': 'abuse', 'safety-concern': 'safety', 'technical-issue': 'bug', 'other': 'other' };
    const description = [formData.description, formData.evidence ? `Evidence: ${formData.evidence}` : ''].filter(Boolean).join('\n\n');
    try {
      await apiService.support.reportIssue({
        email: formData.contactInfo,
        category: categoryMap[formData.reportType] || 'other',
        description,
        reported_user_name: formData.userReported || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err, 'Could not submit your report. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <Card className="p-8 max-w-md mx-auto text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Report Submitted</h2>
          <p className="text-gray-600 mb-6">
            Thank you for reporting this issue. Our safety team will review your report and take appropriate action within 24 hours.
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Reference ID: #FL{Math.random().toString(36).substr(2, 8).toUpperCase()}
          </p>
          <Button
            onClick={onBack}
            className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white"
          >
            Back to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-pink-100 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              onClick={onBack}
              variant="ghost"
              className="flex items-center text-gray-700 hover:text-red-500"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <BrandLockup size="sm" testId="public-brand-lockup" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 bg-clip-text text-transparent">
              Report an Issue
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Help us maintain a safe and positive community by reporting inappropriate behavior, 
            safety concerns, or technical issues.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Report Form */}
          <div className="lg:col-span-2">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
                Report Details
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="report-issue-form">
              {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded" data-testid="report-issue-error">{error}</p>}
                {/* Report Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">What type of issue are you reporting?</label>
                  <div className="grid gap-3">
                    {reportTypes.map((type) => {
                      const Icon = type.icon;
                      return (
                        <label key={type.value} className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                          <input
                            type="radio"
                            name="reportType"
                            value={type.value}
                            checked={formData.reportType === type.value}
                            onChange={handleChange}
                            className="mr-3"
                            required
                          />
                          <Icon className={`w-5 h-5 ${type.color} mr-3`} />
                          <span className="text-gray-900">{type.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* User Reported */}
                {formData.reportType && formData.reportType !== 'technical-issue' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      User you're reporting (if applicable)
                    </label>
                    <Input
                      type="text"
                      name="userReported"
                      value={formData.userReported}
                      onChange={handleChange}
                      placeholder="Username or profile name"
                    />
                  </div>
                )}

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Describe the issue in detail
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Please provide as much detail as possible about what happened, when it occurred, and any other relevant information..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  />
                </div>

                {/* Evidence */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional evidence (optional)
                  </label>
                  <textarea
                    name="evidence"
                    value={formData.evidence}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Screenshots, message content, or other evidence (describe what you have - we may follow up for actual files)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                  />
                </div>

                {/* Contact Info */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your contact information
                  </label>
                  <Input
                    type="email"
                    name="contactInfo"
                    value={formData.contactInfo}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com (for follow-up if needed)"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white py-3"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting Report...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Report
                    </>
                  )}
                </Button>
              </form>
            </Card>
          </div>

          {/* Sidebar Info */}
          <div className="space-y-6">
            {/* What Happens Next */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">What Happens Next?</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <div>
                  <h4 className="font-medium text-gray-900">1. Review (24 hours)</h4>
                  <p>Our safety team reviews your report</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">2. Investigation</h4>
                  <p>We investigate and gather additional information</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">3. Action</h4>
                  <p>Appropriate action is taken based on our findings</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">4. Follow-up</h4>
                  <p>We may contact you if more information is needed</p>
                </div>
              </div>
            </Card>

            {/* Emergency */}
            <Card className="p-6 bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
              <h3 className="text-lg font-semibold text-red-800 mb-2">Emergency Situations</h3>
              <p className="text-red-700 text-sm mb-4">
                If you're in immediate danger or experiencing a crisis, don't wait for our response.
              </p>
              <div className="space-y-2 text-sm">
                <p><strong>Emergency Services:</strong> 911</p>
                <p><strong>Crisis Text Line:</strong> Text HOME to 741741</p>
                <p><strong>National Domestic Violence Hotline:</strong> 1-800-799-7233</p>
              </div>
            </Card>

            {/* Privacy Notice */}
            <Card className="p-6 bg-blue-50 border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Privacy & Confidentiality</h3>
              <p className="text-blue-700 text-sm">
                Your report is confidential and will only be shared with our safety team and relevant authorities if necessary. 
                We take your privacy seriously and will protect your identity throughout the process.
              </p>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportIssues;